import { RefObject, useLayoutEffect, useState } from 'react';

/** Marks an element as one of the items counted by `useFitItems`. */
export const FIT_ITEM_ATTR = 'data-fit-item';
/** Marks the "+N" element whose width is reserved when items are hidden. */
export const FIT_OVERFLOW_ATTR = 'data-fit-overflow';

// Absorbs sub-pixel rounding so an exact fit does not flip between counts.
const FIT_TOLERANCE_PX = 0.5;

interface GetFitCountParams {
    available: number;
    itemWidths: number[];
    overflowWidth: number;
}

/**
 * How many leading items fit in `available` pixels.
 *
 * All items fit: returns the total, no overflow element needed. Otherwise
 * `overflowWidth` is reserved for the "+N" element. Returns 0 when not even
 * the first item fits next to it. A single item always counts as fitting so
 * it can truncate instead of being replaced.
 */
export const getFitCount = ({
    available,
    itemWidths,
    overflowWidth
}: GetFitCountParams): number => {
    const total = itemWidths.length;
    if (total <= 1) {
        return total;
    }
    const limit = available + FIT_TOLERANCE_PX;
    const allWidth = itemWidths.reduce((sum, width) => sum + width, 0);
    if (allWidth <= limit) {
        return total;
    }
    let used = overflowWidth;
    let count = 0;
    for (const width of itemWidths) {
        if (used + width > limit) {
            break;
        }
        used += width;
        count++;
    }
    return count;
};

const getOuterWidth = (element: Element) => {
    const { marginLeft, marginRight } = getComputedStyle(element);
    return (
        element.getBoundingClientRect().width +
        (parseFloat(marginLeft) || 0) +
        (parseFloat(marginRight) || 0)
    );
};

interface UseFitItemsParams {
    /** Single-row container whose children include the marked items. */
    containerRef: RefObject<HTMLElement>;
    /** Number of items the caller renders while measuring. */
    itemCount: number;
    /**
     * Changes whenever the items or anything that affects their width changes
     * (labels, size). A new key triggers a fresh measurement.
     */
    itemsKey: string;
    /** Width kept free at the end of the row, e.g. for a search input. */
    reservedWidth?: number;
}

/**
 * Decides how many items fit on one row of `containerRef`.
 *
 * Returns `null` while a measurement is pending. In that state the caller must
 * render every item (each marked `data-fit-item`) plus a "+N" probe marked
 * `data-fit-overflow`. The measurement runs in a layout effect, so the
 * measuring render is never painted. Afterwards it returns the number of items
 * to render, and the caller renders the overflow element for the rest.
 *
 * Re-measures when `itemsKey` changes, when the container width changes and
 * when web fonts finish loading. The container's width must not depend on its
 * content (block, stretched or fixed-width parents): in shrink-to-fit parents
 * the measurement is unreliable, so a final overflow check drops items until
 * the row no longer overflows.
 * Does nothing useful in environments without layout (jsdom): a zero-width
 * container reports that everything fits.
 */
export const useFitItems = ({
    containerRef,
    itemCount,
    itemsKey,
    reservedWidth = 0
}: UseFitItemsParams): number | null => {
    const [resizeCount, setResizeCount] = useState(0);
    const key = `${itemsKey}|${resizeCount}`;
    const [fit, setFit] = useState<{ key: string; count: number } | null>(null);
    const isMeasuring = fit?.key !== key;

    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!isMeasuring || !container) {
            return;
        }
        const width = container.clientWidth;
        if (width === 0) {
            setFit({ key, count: itemCount });
            return;
        }
        const { paddingLeft, paddingRight } = getComputedStyle(container);
        const available =
            width -
            (parseFloat(paddingLeft) || 0) -
            (parseFloat(paddingRight) || 0) -
            reservedWidth;
        const overflow = container.querySelector(`[${FIT_OVERFLOW_ATTR}]`);
        const itemWidths = Array.from(
            container.querySelectorAll(`[${FIT_ITEM_ATTR}]`)
        ).map(getOuterWidth);
        setFit({
            key,
            count: getFitCount({
                available,
                itemWidths,
                overflowWidth: overflow ? getOuterWidth(overflow) : 0
            })
        });
    }, [containerRef, isMeasuring, itemCount, key, reservedWidth]);

    // Safety net for layouts where the container's width depends on its
    // content (shrink-to-fit parents), which makes the measurement unreliable:
    // never leave the committed row overflowing, drop one more item instead.
    useLayoutEffect(() => {
        const container = containerRef.current;
        if (
            isMeasuring ||
            !container ||
            itemCount <= 1 ||
            fit.count === 0 ||
            container.scrollWidth <= container.clientWidth + FIT_TOLERANCE_PX
        ) {
            return;
        }
        setFit({ key, count: fit.count - 1 });
    }, [containerRef, fit, isMeasuring, itemCount, key]);

    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container || typeof ResizeObserver === 'undefined') {
            return;
        }
        let lastWidth = container.clientWidth;
        const observer = new ResizeObserver(() => {
            const width = container.clientWidth;
            if (width !== lastWidth) {
                lastWidth = width;
                setResizeCount(count => count + 1);
            }
        });
        observer.observe(container);
        return () => observer.disconnect();
    }, [containerRef]);

    // Web fonts load lazily on first use, so item widths can change after the
    // first measurement without the container resizing.
    useLayoutEffect(() => {
        const fonts = typeof document !== 'undefined' && document.fonts;
        if (!fonts?.addEventListener) {
            return;
        }
        const remeasure = () => setResizeCount(count => count + 1);
        fonts.addEventListener('loadingdone', remeasure);
        return () => fonts.removeEventListener('loadingdone', remeasure);
    }, []);

    return isMeasuring ? null : fit.count;
};
